import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { TimerReset } from "lucide-react";
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Inactivity limits — sessionStorage overrides exist purely for QA.
const IDLE_LIMIT_MS = Number(sessionStorage.getItem("av_idle_ms")) || 30 * 60 * 1000;
const WARNING_MS = Number(sessionStorage.getItem("av_warn_ms")) || 2 * 60 * 1000;
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];

export const SessionTimeoutGuard = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const lastActivity = useRef(Date.now());
    const [secondsLeft, setSecondsLeft] = useState(null);
    const warningOpen = secondsLeft != null;
    const warningRef = useRef(false);
    warningRef.current = warningOpen;

    useEffect(() => {
        if (!user) {
            setSecondsLeft(null);
            return undefined;
        }
        lastActivity.current = Date.now();
        const onActivity = () => {
            // While the warning is up, only the explicit button keeps the session.
            if (!warningRef.current) lastActivity.current = Date.now();
        };
        ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
        const tick = setInterval(() => {
            const idle = Date.now() - lastActivity.current;
            if (idle >= IDLE_LIMIT_MS) {
                setSecondsLeft(null);
                logout();
                toast.info("You were signed out after a period of inactivity.");
                navigate("/login");
            } else if (idle >= IDLE_LIMIT_MS - WARNING_MS) {
                setSecondsLeft(Math.ceil((IDLE_LIMIT_MS - idle) / 1000));
            } else {
                setSecondsLeft(null);
            }
        }, 1000);
        return () => {
            ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
            clearInterval(tick);
        };
    }, [user, logout, navigate]);

    const staySignedIn = useCallback(async () => {
        lastActivity.current = Date.now();
        setSecondsLeft(null);
        try {
            await api.post("/auth/refresh"); // slides the httpOnly cookie expiry
        } catch {
            // a failed refresh surfaces via the idle guard itself
        }
    }, []);

    if (!user) return null;
    return (
        <AlertDialog open={warningOpen}>
            <AlertDialogContent className="max-w-md" data-testid="session-timeout-dialog">
                <AlertDialogHeader>
                    <div className="w-10 h-10 rounded-full border border-border grid place-items-center mb-2">
                        <TimerReset className="h-5 w-5" strokeWidth={1.5} />
                    </div>
                    <AlertDialogTitle className="font-display">
                        Still there?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                        For your security, you&#39;ll be signed out in{" "}
                        <span className="font-semibold text-foreground tabular-nums" data-testid="session-timeout-countdown">
                            {secondsLeft}s
                        </span>{" "}
                        due to inactivity. Unsaved changes may be lost.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <Button
                        variant="outline"
                        className="rounded-md font-display"
                        data-testid="session-signout-now-btn"
                        onClick={() => {
                            setSecondsLeft(null);
                            logout();
                            navigate("/login");
                        }}
                    >
                        Sign out now
                    </Button>
                    <Button
                        className="rounded-md font-display"
                        data-testid="session-stay-signed-in-btn"
                        onClick={staySignedIn}
                    >
                        Stay signed in
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};
